import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogResult } from '@progress/kendo-angular-dialog';
import { FileInfo } from '@progress/kendo-angular-upload';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ManageCostEstimationComponent } from 'src/app/feature-module/cost-estimations/component/manage-cost-estimation/manage-cost-estimation.component';
import { CostEstimation, CostEstimationFormBuilder } from 'src/app/feature-module/cost-estimations/models/CostEstimation';
import { CostEstimationPart } from 'src/app/feature-module/cost-estimations/models/CostEstimationPart';
import { CostEstimationStatus } from 'src/app/feature-module/cost-estimations/models/CostEstimationStatus';
import { CostEstimationType } from 'src/app/feature-module/cost-estimations/models/CostEstimationType';
import { CostEstimationService } from 'src/app/feature-module/cost-estimations/service/cost-estimation.service';
import { ShowDocumentComponent } from 'src/app/feature-module/documents/component/show-document/show-document.component';
import { CustomDialogSettings } from 'src/app/shared/model/CustomDialogSettings';
import { CustomDialogService } from 'src/app/shared/services/custom-dialog.service';
import { AudanetCalculation } from '../../models/AudanetCalculation';
import { AudanetCalculationAttachment } from '../../models/AudanetCalculationAttachment';
import { AudanetOperationType } from '../../models/AudanetOperationType';
import { AudanetTask } from '../../models/AudanetTask';
import { AudanetIntegrationService } from '../../service/audanet-integration.service';
import { ShowCalculationDetailsComponent } from '../show-calculation-details/show-calculation-details.component';
import { DialogAlignment } from 'src/app/shared/model/DialogAlignment';
import { AudanetWindowSyncService } from '../../service/audanet-window-sync.service';

@Component({
  selector: 'app-show-audanet-task-details',
  templateUrl: './show-audanet-task-details.component.html',
  styleUrls: ['./show-audanet-task-details.component.scss']
})
export class ShowAudanetTaskDetailsComponent implements OnInit {

  constructor(private activatedRoute: ActivatedRoute,
    private costEstimationService: CostEstimationService,
    private audanetIntegrationService: AudanetIntegrationService,
    private customDialogService : CustomDialogService,
    private translateService: TranslateService,
    private windowSync: AudanetWindowSyncService) { }

  task$: Observable<AudanetTask>;
  isLoading: boolean;

  claimId: string;
  audanetOperationType: AudanetOperationType;
  taskId: string;
  costEstimationId:number;

  importStandardCostEstimation = AudanetOperationType.ImportStandardCostEstimation;
  importAdditionalCostEstimation = AudanetOperationType.ImportAdditionalCostEstimation;

  updateStandardCostEstimation = AudanetOperationType.UpdateStandardCostEstimation;
  updateAdditionalCostEstimation = AudanetOperationType.UpdateAdditionalCostEstimation;

  updateAcceptedDataOfCostEstimation = AudanetOperationType.UpdateAceptedDataOfCostEstimation;

  @ViewChild("audanetTaskDialog", { read: ViewContainerRef })
  public containerRef: ViewContainerRef;

  ngOnInit(): void {

    this.taskId = this.activatedRoute.snapshot.params.taskId;
    this.claimId = this.activatedRoute.snapshot.params.claimId;
    this.costEstimationId = this.activatedRoute.snapshot.params.costEstimationId;
    this.audanetOperationType = this.activatedRoute.snapshot.params.audanetOperationType;

    this.getTaskDetails();

  }

  getTaskDetails() {

    this.isLoading = true;

    this.task$ = this.audanetIntegrationService.getTask(this.taskId).pipe(
      tap(() => { this.isLoading = false; })
    );
  }

  showDocument(attachment: AudanetCalculationAttachment) {

    const dialogSettings: CustomDialogSettings = {
      content: ShowDocumentComponent,
      title: `${this.translateService.instant('claims-center.claimDocumentation.showContent')}`,
      minHeight: '25vh',
      minWidth: '25vw',
      maxHeight: '90vh',
      maxWidth: '90vw',
      appendTo:this.containerRef,
      dialogInputs: {
        documentContent: this.audanetIntegrationService.base64toBlob(attachment.base64Content, attachment.fileExtension),
        fileExtension : attachment.fileExtension,
        fileName : attachment.fileName
      }
    };

    this.customDialogService.showDialog(dialogSettings);

  }

  showDetails(calculation: AudanetCalculation) {

    const dialogSettings: CustomDialogSettings = {
      content: ShowCalculationDetailsComponent,
      title: `${this.translateService.instant('claims-center.audanetIntegration.showCalculationDetails')}`,
      minHeight: '25vh',
      minWidth: '25vw',
      maxHeight: '90vh',
      maxWidth: '90vw',
      appendTo:this.containerRef,
      dialogInputs: {
        calculation: calculation
      }
    };

    this.customDialogService.showDialog(dialogSettings);
  }

  importStandardEstimation(calculation: AudanetCalculation) {
    this.showImportDialog(calculation, CostEstimationType.Standard);
  }

  
  importAdditionalEstimation(calculation: AudanetCalculation) {
    this.showImportDialog(calculation, CostEstimationType.Additional);
  }

  showImportDialog(calculation: AudanetCalculation, costEstimationTypeId: number) {
    const costEstimation = CostEstimationFormBuilder.initCostEstimation();
    costEstimation.costEstimationTypeId = costEstimationTypeId;
    costEstimation.costEstimationStatusId = CostEstimationStatus.InProgress;
    costEstimation.claimIdentifier = this.claimId;
    costEstimation.netAmount = calculation.totalNetAmount ? calculation.totalNetAmount : 0.00;
    costEstimation.vatAmount = calculation.totalTaxAmount ? calculation.totalTaxAmount : 0.00;
    costEstimation.carBodyLabour = calculation.labour ? calculation.labour : 0.00;
    costEstimation.alternativeParts = 0.00; //no idea yet how I can get it from audanet 
    costEstimation.normalies = calculation.normalies ? calculation.normalies : 0.00;
    costEstimation.originalParts = calculation.partsAmount ? calculation.partsAmount : 0.00;
    costEstimation.paintLabour = calculation.paintLabour ? calculation.paintLabour : 0.00;
    costEstimation.paintMaterials = calculation.paintMaterials ? calculation.paintMaterials : 0.00;
    costEstimation.additionalCosts = calculation.additionalCosts ? calculation.additionalCosts : 0.00;
    costEstimation.deductibleAmount = calculation.deductibleAmount ? -calculation.deductibleAmount : 0.00;
    costEstimation.pdrLabour = 0.00;
    costEstimation.audanetCalculationId = calculation.calculationId;
    costEstimation.audanetTaskId = this.taskId


    calculation.audanetCalculationParts.forEach((item) => {
      const part : CostEstimationPart = {
        costEstimationId: 0,
        id: 0,
        description: item.description,
        code: item.partIdentifier,
        quantity: item.quantity,
        price: item.price,
        includeInPartsOrder: true,
        reasonForNotOrdering: ''
      };
      costEstimation.costEstimationParts.push(part);
    })
    

    const attachments = [];
    calculation.audanetCalculationAttachments.forEach((item) => {

      if (item.fileExtension == 'pdf') {
        const filePart: Blob[] = [];
        filePart.push(this.audanetIntegrationService.base64toBlob(item.base64Content, item.fileExtension));
        const attachment: FileInfo = {
          name: item.fileName,
          extension: "." + item.fileExtension,
          validationErrors: null,
          rawFile: new File(filePart, item.fileName),
          state: 2,
          size: filePart.length
        };
        attachments.push(attachment);
      }
    }
    );


    const dialogSettings: CustomDialogSettings = {
      content: ManageCostEstimationComponent,
      maxHeight: '90vh',
      maxWidth: '90vw',
      appendTo:this.containerRef,
      dialogAlignment: DialogAlignment.bottom,
      dialogInputs: {
        estimation: costEstimation,
        filesToAttachOnInit : attachments
      },
      resultCallback: (r: DialogResult) => this.notifyParent(r)
    };

    this.customDialogService.showDialog(dialogSettings);

  }

  private notifyParent(r: DialogResult) {
    if (r['text'] == 'OK') {
      this.windowSync.notifyCostEstimationChanged({
        claimIdentifier: this.claimId,
        costEstimationId: this.costEstimationId ? Number(this.costEstimationId) : undefined
      });
    }
  }

  updateEstimation(calculation: AudanetCalculation) {

    this.costEstimationService.getCostEstimation(this.costEstimationId).subscribe(
      (r) =>  this.showUpdateDialog(r,calculation));
  }
    
   showUpdateDialog(costEstimation : CostEstimation, calculation: AudanetCalculation){

    costEstimation.netAmount = calculation.totalNetAmount ? calculation.totalNetAmount : 0.00;
    costEstimation.carBodyLabour = calculation.labour ? calculation.labour : 0.00;
    costEstimation.alternativeParts = 0.00; //no idea yet how I can get it from audanet 
    costEstimation.normalies = calculation.normalies ? calculation.normalies : 0.00;
    costEstimation.originalParts = calculation.partsAmount ? calculation.partsAmount : 0.00;
    costEstimation.paintLabour = calculation.paintLabour ? calculation.paintLabour : 0.00;
    costEstimation.paintMaterials = calculation.paintMaterials ? calculation.paintMaterials : 0.00;
    costEstimation.additionalCosts = calculation.additionalCosts ? calculation.additionalCosts : 0.00;
    costEstimation.deductibleAmount = calculation.deductibleAmount ? -calculation.deductibleAmount : 0.00;
    costEstimation.netAmount = calculation.totalNetAmount ? calculation.totalNetAmount : 0.00;
    costEstimation.vatAmount = calculation.totalTaxAmount ? calculation.totalTaxAmount : 0.00;
    costEstimation.pdrLabour = 0.00;
    costEstimation.audanetCalculationId = calculation.calculationId;
    
    costEstimation.costEstimationParts =[];
    calculation.audanetCalculationParts.forEach((item) => {
      const part : CostEstimationPart = {
        costEstimationId: 0,
        id: 0,
        description: item.description,
        code: item.partIdentifier,
        quantity: item.quantity,
        price: item.price,
        includeInPartsOrder: true,
        reasonForNotOrdering: ''
      };
      costEstimation.costEstimationParts.push(part);
    })

    const attachments = [];
    calculation.audanetCalculationAttachments.forEach((item) => {

      if (item.fileExtension == 'pdf') {

        const filePart: Blob[] = [];
        filePart.push(this.audanetIntegrationService.base64toBlob(item.base64Content, item.fileExtension));
        const attachment: FileInfo = {
          name: item.fileName,
          extension: "." + item.fileExtension,
          validationErrors: null,
          rawFile: new File(filePart, item.fileName),
          state: 2,
          size: filePart.length
        };
        attachments.push(attachment);
      }
    }

    );



    const dialogSettings: CustomDialogSettings = {
      content: ManageCostEstimationComponent,
      maxHeight: '90vh',
      maxWidth: '90vw',
      appendTo:this.containerRef,
      dialogAlignment: DialogAlignment.bottom,
      dialogInputs: {
        estimation: costEstimation,
        filesToAttachOnInit : attachments
      },
      resultCallback: (r: DialogResult) => this.notifyParent(r)
    };

    this.customDialogService.showDialog(dialogSettings);
  }

  updateEstimationAcceptedData(calculation: AudanetCalculation) {

    this.costEstimationService.getCostEstimation(this.costEstimationId).subscribe(
      (r) =>  this.showUpdateDialogForAcceptedValues(r,calculation));
  }

  showUpdateDialogForAcceptedValues(costEstimation : CostEstimation, calculation: AudanetCalculation){

    costEstimation.acceptedAmount = calculation.totalNetAmount;
    costEstimation.vatAmount = calculation.totalTaxAmount;
    costEstimation.carBodyLabourAccepted = calculation.labour;
    costEstimation.alternativePartsAccepted = 0.00; //no idea yet how I can get it from audanet 
    costEstimation.normaliesAccepted = calculation.normalies;
    costEstimation.originalPartsAccepted = calculation.partsAmount;
    costEstimation.paintLabourAccepted = calculation.paintLabour;
    costEstimation.paintMaterialsAccepted = calculation.paintMaterials;
    costEstimation.additionalCostsAccepted = calculation.additionalCosts;
    costEstimation.deductibleAmountAccepted = -calculation.deductibleAmount;
    costEstimation.pdrLabourAccepted = 0.00;

    const attachments = [];
    calculation.audanetCalculationAttachments.forEach((item) => {

      if (item.fileExtension == 'pdf') {

        const filePart: Blob[] = [];
        filePart.push(this.audanetIntegrationService.base64toBlob(item.base64Content, item.fileExtension));
        const attachment: FileInfo = {
          name: item.fileName,
          extension: "." + item.fileExtension,
          validationErrors: null,
          rawFile: new File(filePart, item.fileName),
          state: 2,
          size: filePart.length
        };
        attachments.push(attachment);
      }
    }

    );


    const dialogSettings: CustomDialogSettings = {
      content: ManageCostEstimationComponent,
      maxHeight: '90vh',
      maxWidth: '90vw',
      appendTo:this.containerRef,
      dialogAlignment: DialogAlignment.bottom,
      dialogInputs: {
        estimation: costEstimation,
        filesToAttachOnInit : attachments,
        acceptingValuesFromAudanet : true 
      },
      resultCallback: (r: DialogResult) => this.notifyParent(r)
    };

    this.customDialogService.showDialog(dialogSettings);

  }

}
